import { type FC, type KeyboardEvent, type ReactNode } from 'react';

import {
  detailListItemBadgeSpacerClass,
  detailListItemClass,
  detailListItemClickableClass,
  detailListItemTextColumnClass,
  detailListItemTruncateClass,
} from '@eneco/custom-components/flows/DetailListItem.css';
import { Box, Divider, Stack, Text } from '@eneco/sparky';
import { ChevronRightIcon, EditIcon, InfoIcon } from '@eneco/sparky/icons';

import { BadgeIcon } from './BadgeIcon';

export type DetailListItemTrailingAction = 'arrow' | 'information' | 'pencil';

export interface DetailListItemProps {
  /** Sparky icon rendered inside {@link BadgeIcon}, e.g. `<ProfileIcon color="iconPrimary" />`. */
  icon: ReactNode;
  /** Primary line of text. */
  title: ReactNode;
  /** Optional secondary line beneath the title. */
  description?: ReactNode;
  /** Font weight for the title. Defaults to `bold`. */
  titleWeight?: 'bold' | 'regular';
  /** Font weight for the description. Defaults to `regular`. */
  descriptionWeight?: 'bold' | 'regular';
  /** Trailing indicator shown on the right side of the row. */
  trailingAction?: DetailListItemTrailingAction;
  /** Optional text shown on the right, optionally before the trailing icon, e.g. `"Ja"`. */
  trailingText?: ReactNode;
  /** Called when the row is clicked. */
  onClick?: () => void;
  /** Render a divider under the text column only. Set by {@link DetailList}. */
  withDivider?: boolean;
}

const trailingIcons = {
  arrow: ChevronRightIcon,
  information: InfoIcon,
  pencil: EditIcon,
} as const;

export const DetailListItem: FC<DetailListItemProps> = ({
  icon,
  title,
  description,
  titleWeight = 'bold',
  descriptionWeight = 'regular',
  trailingAction,
  trailingText,
  onClick,
  withDivider = false,
}) => {
  const TrailingIcon = trailingAction ? trailingIcons[trailingAction] : null;
  const isInteractive = Boolean(onClick);

  const handleKeyDown = (event: KeyboardEvent<HTMLLIElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick?.();
    }
  };

  return (
    <li
      className={isInteractive ? `${detailListItemClass} ${detailListItemClickableClass}` : detailListItemClass}
      onClick={onClick}
      onKeyDown={isInteractive ? handleKeyDown : undefined}
      role={isInteractive ? 'button' : undefined}
      tabIndex={isInteractive ? 0 : undefined}>
      <Stack>
        <Box padding="5">
          <Stack direction="row" gap="4" alignY="center">
            <Stack.Item shrink={false}>
              <BadgeIcon>{icon}</BadgeIcon>
            </Stack.Item>

            <Stack.Item grow className={detailListItemTextColumnClass}>
              <Stack direction="row" gap="4" alignY="center">
                <Stack.Item grow className={detailListItemTextColumnClass}>
                  <Stack>
                    <Text
                      size="BodyM"
                      weight={titleWeight}
                      whiteSpace="nowrap"
                      display="block"
                      className={detailListItemTruncateClass}>
                      {title}
                    </Text>
                    {description ? (
                      <Text
                        size="BodyS"
                        color={descriptionWeight === 'bold' ? 'textPrimary' : 'textLowEmphasis'}
                        weight={descriptionWeight}
                        whiteSpace="nowrap"
                        display="block"
                        className={detailListItemTruncateClass}>
                        {description}
                      </Text>
                    ) : null}
                  </Stack>
                </Stack.Item>

                {(TrailingIcon || trailingText) ? (
                  <Stack.Item shrink={false}>
                    <Stack direction="row" gap="2" alignY="center">
                      {trailingText ? (
                        <Text size="BodyS" weight="bold">
                          {trailingText}
                        </Text>
                      ) : null}
                      {TrailingIcon ? <TrailingIcon color="iconPrimary" size="medium" /> : null}
                    </Stack>
                  </Stack.Item>
                ) : null}
              </Stack>
            </Stack.Item>
          </Stack>
        </Box>

        {withDivider ? (
          <Box paddingX="5">
            <Stack direction="row" gap="4">
              <Stack.Item shrink={false}>
                <span className={detailListItemBadgeSpacerClass} aria-hidden>
                  <BadgeIcon>{icon}</BadgeIcon>
                </span>
              </Stack.Item>
              <Stack.Item grow className={detailListItemTextColumnClass}>
                <Divider emphasis="medium" />
              </Stack.Item>
            </Stack>
          </Box>
        ) : null}
      </Stack>
    </li>
  );
};
